/**
 * "ALREADY THERE:" AND "YA ESTABA:", A COPY (ops #98, business #47 answer 2, business #49,
 * `docs/plan-client-language.md` section 7).
 *
 * The business decided that a cleaner notes damage already there before the clean as an issue of kind
 * `other`, its line starting "Already there:", never kind `damage`, which says we caused it
 * (`docs/damage-process.md` section 3), and that the office writes what the client reads of that line
 * in the client's language, "Ya estaba:" in Spanish (`docs/client-messages.md` rule 6). These are the
 * two phrases, copied word for word from those two documents, and nothing here composes one from the
 * other, translates either or adds a third: a language the business has not written a phrase for has
 * none. `api/test/already-there.test.ts` reads the sibling checkout and fails the gate when either
 * document no longer carries its phrase.
 *
 * One definition, two consumers: the issue's door (`api/src/records/reports.ts`
 * `writeIssueForClient`) refuses a line marked already there that does not open with the phrase of
 * the household's language, and the Issues panel names that phrase beside its tick and starts the
 * tick where the line as written opens with the English one, a convenience and never the fact. A
 * cleaner whose app speaks another language writes the opening in it, which ops does not read and
 * does not try to; the person in the office does.
 */
import type { ClientLanguage } from './enums.ts';

/** The opening of the office's line for the client on damage already there, by the household's language. */
export const ALREADY_THERE: Readonly<Record<ClientLanguage, string>> = Object.freeze({
  en: 'Already there:',
  es: 'Ya estaba:',
});

/** Where each phrase came from, named so a reader can go and check it. */
export const ALREADY_THERE_SOURCE: Readonly<Record<ClientLanguage, string>> = Object.freeze({
  en: 'faithfully-kept-business/docs/damage-process.md section 3 (business fa2e332)',
  es: 'faithfully-kept-business/docs/client-messages.md rule 6, client-messages-2026-10-24.8 (business a4abeb6)',
});

/** The day the business's two documents were read to write the phrases above. */
export const ALREADY_THERE_READ_ON = '2026-10-25';

/** True where the words open with the phrase of `language`, word for word and in its own case. */
export function opensAlreadyThere(language: ClientLanguage, words: string): boolean {
  return words.trimStart().startsWith(ALREADY_THERE[language]);
}
